<template>
  <div style="position: relative;">
    <div v-if="invoiceData.payment_status === 'not_paid'" class="not-paid-watermark">{{ $t('invoice.notPaidWatermark') }}</div>

    <!-- Header: Logo + Company Info -->
    <table style="width: 100%" id="head-table">
      <tr>
        <td style="width: 130px;" class="print-pl-20">
          <img
            style="width: 110px; height: 100px"
            id="header-img"
            src="../../../assets/images/black_shipzone.png"
            alt=""
          />
        </td>
        <td>
          <table id="company-info" style="width: 100%;" class="print-ml-0">
            <tr class="header-table">
              <td style="font-size: 14px; font-weight: bold;" class="print-pr-20">{{ companyData.company_name }}</td>
            </tr>
            <tr class="header-table">
              <td style="font-size: 14px" class="print-pr-20">{{ companyData.company_address }}</td>
            </tr>
            <tr class="header-table">
              <td style="font-size: 14px" class="print-pr-20">{{ companyData.phone_number }}</td>
            </tr>
            <tr class="header-table">
              <td style="font-size: 14px" class="print-pr-20">{{ companyData.company_email }}</td>
            </tr>
          </table>
        </td>
      </tr>
    </table>

    <!-- Title -->
    <table style="width: 100%; margin-top: 10px;" id="print-table1">
      <tr>
        <td style="text-align: center; padding: 8px 0;">
          <strong style="font-size: 22px; text-transform: uppercase;">{{ $t('invoice.shippingInvoiceTitle') }}</strong>
        </td>
      </tr>
    </table>

    <!-- Invoice Info + Invoiced To -->
    <table style="width: 100%; margin-bottom: 10px; border: 1px solid #000;" id="print-table2">
      <tr>
        <td style="padding: 10px; width: 50%; vertical-align: top; border-right: 1px solid #000;">
          <p style="font-size: 14px; margin: 3px 0;">
            <strong>{{ $t('columns.invoiceNumber') }}:</strong>
            <span> {{ invoiceData.invoice_number }}</span>
          </p>
          <p style="font-size: 14px; margin: 3px 0;">
            <strong>{{ $t('customer.invoiceDate') }}:</strong>
            <span> {{ invoiceData.created_at }}</span>
          </p>
          <p v-if="invoiceData.weight" style="font-size: 14px; margin: 3px 0;">
            <strong>{{ $t('columns.weight') }}:</strong>
            <span> {{ invoiceData.weight }}</span>
          </p>
          <p v-if="invoiceData.volume" style="font-size: 14px; margin: 3px 0;">
            <strong>{{ $t('columns.volume') }}:</strong>
            <span> {{ invoiceData.volume }}</span>
          </p>
          <p v-if="paymentMethodLabel" style="font-size: 14px; margin: 3px 0;">
            <strong>{{ $t('columns.paymentMethod') }}:</strong>
            <span style="color: #2e7d32; font-weight: bold;"> {{ paymentMethodLabel }}</span>
          </p>
          <p v-if="paymentStatusLabel" style="font-size: 14px; margin: 3px 0;">
            <strong>{{ $t('reports.paymentStatus') }}:</strong>
            <span :style="{ backgroundColor: paymentStatusColor.bg, color: paymentStatusColor.color, padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold', display: 'inline-block' }"> {{ paymentStatusLabel }}</span>
          </p>
          <p v-if="invoiceData.payment_status && invoiceData.payment_status !== 'paid' && invoiceData.payment_status !== 'cancel'" style="font-size: 14px; margin: 3px 0;">
            <strong>{{ $t('invoice.paidStatus') }}:</strong> $ {{ Number(invoiceData.paid_amount || 0).toFixed(2) }}
            &nbsp;&nbsp;
            <strong>{{ $t('columns.remaining') }}:</strong> <span style="color: #c0392b; font-weight: bold;">$ {{ Number(invoiceData.remaining_amount || 0).toFixed(2) }}</span>
          </p>
        </td>
        <td style="padding: 10px; width: 50%; vertical-align: top;">
          <p style="font-size: 14px; font-weight: bold; margin: 0 0 6px 0; text-decoration: underline;">{{ $t('delivery.invoicedToLabel') }}</p>
          <p style="font-size: 14px; margin: 3px 0; font-weight: bold; background-color: yellow; color: black; display: inline-block;">
            {{ invoiceData.customer ? invoiceData.customer.full_name : '' }}
          </p>
          <p v-if="invoiceData.is_pickup" style="font-size: 14px; margin: 3px 0;">
            <strong>{{ $t('delivery.deliveryMethodLabel') }}</strong>
            <span style="color: #e65100; font-weight: bold;">{{ $t('delivery.pickupInOfficeLabel') }}</span>
          </p>
          <p v-else-if="invoiceData.delivery_address || (invoiceData.customer && invoiceData.customer.address)" style="font-size: 14px; margin: 3px 0;">
            <strong>{{ $t('columns.deliveryAddress') }}:</strong>
            {{ invoiceData.delivery_address || invoiceData.customer.address }}
          </p>
          <p style="font-size: 14px; margin: 3px 0;">
            {{ $t('logs.phoneColon') }} {{ invoiceData.customer ? invoiceData.customer.phone_number : '' }}
          </p>
          <p v-if="invoiceData.customer && invoiceData.customer.email" style="font-size: 14px; margin: 3px 0;">
            {{ $t('logs.emailColon') }} {{ invoiceData.customer.email }}
          </p>
        </td>
      </tr>
    </table>

    <!-- Items Table -->
    <table style="width: 100%; border-collapse: collapse; border: 1px solid #000;" id="items-table">
      <thead>
        <tr style="background-color: #f2f2f2; font-size: 13px;">
          <th style="border: 1px solid #000; padding: 6px; text-align: center;">#</th>
          <th style="border: 1px solid #000; padding: 6px;">{{ $t('delivery.orderNumberColumn') }}</th>
          <th style="border: 1px solid #000; padding: 6px;">{{ $t('columns.productName') }}</th>
          <th style="border: 1px solid #000; padding: 6px; text-align: center;">{{ $t('columns.size') }}</th>
          <th style="border: 1px solid #000; padding: 6px; text-align: right;">{{ $t('columns.price') }}</th>
          <th style="border: 1px solid #000; padding: 6px; text-align: center;">{{ $t('columns.qty') }}</th>
          <th style="border: 1px solid #000; padding: 6px; text-align: right;">{{ $t('columns.total') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(order, index) in allOrders"
          :key="order.order_id || index"
          style="font-size: 13px;"
        >
          <td style="border: 1px solid #000; padding: 5px; text-align: center;">{{ index + 1 }}</td>
          <td style="border: 1px solid #000; padding: 5px;">{{ order.theOrderID }}</td>
          <td style="border: 1px solid #000; padding: 5px;">{{ order.product_name }}</td>
          <td style="border: 1px solid #000; padding: 5px; text-align: center;">{{ order.size }}</td>
          <td style="border: 1px solid #000; padding: 5px; text-align: right;">$ {{ orderUnitPriceCombined(order).toFixed(2) }}</td>
          <td style="border: 1px solid #000; padding: 5px; text-align: center;">{{ order.quantity }}</td>
          <td style="border: 1px solid #000; padding: 5px; text-align: right;">$ {{ orderLineTotalCombined(order).toFixed(2) }}</td>
        </tr>
      </tbody>
    </table>

    <!-- Fees + Totals -->
    <table style="width: 100%; border-collapse: collapse; margin-top: 0; table-layout: fixed;">
      <colgroup>
        <col style="width: 160px;" />
        <col />
      </colgroup>
      <!-- Int. shipping is rolled into each line's Price / Total above (per unit item + per unit int. ship.) × qty -->
      <tr v-if="invoiceData.shipment > 0" style="font-size: 14px;">
        <td style="border: 1px solid #000; padding: 4px 8px; font-weight: bold; width: 160px;">{{ $t('columns.shipment') }}:</td>
        <td style="border: 1px solid #000; padding: 4px 8px; text-align: right;">$ {{ invoiceData.shipment }}</td>
      </tr>
      <tr v-if="invoiceData.handling_fee > 0" style="font-size: 14px;">
        <td style="border: 1px solid #000; padding: 4px 8px; font-weight: bold;">{{ $t('invoice.handlingFeeLabel') }}:</td>
        <td style="border: 1px solid #000; padding: 4px 8px; text-align: right;">$ {{ invoiceData.handling_fee }}</td>
      </tr>
      <tr v-if="Number(invoiceData.tax) > 0" style="font-size: 14px;">
        <td style="border: 1px solid #000; padding: 4px 8px; font-weight: bold;">{{ $t('columns.tax') }}:</td>
        <td style="border: 1px solid #000; padding: 4px 8px; text-align: right;">$ {{ invoiceData.tax }}</td>
      </tr>
      <!-- Invoice-level delivery_price (USD field) -->
      <tr v-if="Number(invoiceData.delivery_price) > 0" style="font-size: 14px;">
        <td style="border: 1px solid #000; padding: 4px 8px; font-weight: bold;">{{ $t('logs.deliveryIraqiPriceColon') }}</td>
        <td style="border: 1px solid #000; padding: 4px 8px; text-align: right;">{{ deliveryIqd }} IQD &nbsp;/&nbsp; $ {{ Number(invoiceData.delivery_price).toFixed(2) }}</td>
      </tr>
      <!-- Invoice-level local_delivery (IQD field) -->
      <tr v-if="Number(invoiceData.local_delivery) > 0" style="font-size: 14px;">
        <td style="border: 1px solid #000; padding: 4px 8px; font-weight: bold;">{{ $t('logs.deliveryColon') }}</td>
        <td style="border: 1px solid #000; padding: 4px 8px; text-align: right;">{{ localDeliveryIqd }} IQD &nbsp;/&nbsp; $ {{ localDeliveryUsd }}</td>
      </tr>
      <!-- Order-level delivery_price (USD) summed across all orders -->
      <tr v-if="ordersDeliveryUsdTotal > 0" style="font-size: 14px;">
        <td style="border: 1px solid #000; padding: 4px 8px; font-weight: bold;">{{ $t('logs.deliveryIraqiPriceColon') }}</td>
        <td style="border: 1px solid #000; padding: 4px 8px; text-align: right;">{{ ordersDeliveryIqd }} IQD &nbsp;/&nbsp; $ {{ ordersDeliveryUsdTotal.toFixed(2) }}</td>
      </tr>
      <!-- Order-level local_delivery (IQD) summed across all orders -->
      <tr v-if="ordersLocalDeliveryIqdTotal > 0" style="font-size: 14px;">
        <td style="border: 1px solid #000; padding: 4px 8px; font-weight: bold;">{{ $t('logs.deliveryColon') }}</td>
        <td style="border: 1px solid #000; padding: 4px 8px; text-align: right;">{{ ordersLocalDeliveryIqd }} IQD &nbsp;/&nbsp; $ {{ ordersLocalDeliveryUsd }}</td>
      </tr>
      <tr v-if="invoiceData.discount > 0" style="font-size: 14px;">
        <td style="border: 1px solid #000; padding: 4px 8px; font-weight: bold;">{{ $t('columns.discount') }}:</td>
        <td style="border: 1px solid #000; padding: 4px 8px; text-align: right;">$ {{ invoiceData.discount }}</td>
      </tr>
      <tr style="font-size: 15px; font-weight: bold;">
        <td style="border: 1px solid #000; padding: 6px 8px;">{{ $t('delivery.totalUsdLabel') }}</td>
        <td style="border: 1px solid #000; padding: 6px 8px; text-align: right;"><span style="background-color: yellow; color: black;">$ {{ totalUsd }}</span></td>
      </tr>
      <tr style="font-size: 15px; font-weight: bold;">
        <td style="border: 1px solid #000; padding: 6px 8px;">{{ $t('delivery.iraqiDinarLabel') }}</td>
        <td style="border: 1px solid #000; padding: 6px 8px; text-align: right;">{{ totalIqd }} IQD</td>
      </tr>
    </table>

    <!-- Note -->
    <p v-if="invoiceData.note" style="margin-top: 10px; font-size: 14px;">
      <strong>{{ $t('columns.note') }}:</strong> {{ invoiceData.note }}
    </p>
    <p v-if="invoiceData.second_orders && invoiceData.second_orders.length > 0" style="font-size: 14px; margin-top: 4px;">
      <strong>{{ $t('invoice.invoiceNoteLabel') }}</strong> {{ $t('invoice.secondaryInvoiceGeneratedNote') }}
    </p>

    <!-- Signature -->
    <table style="width: 100%; margin-top: 30px;">
      <tr>
        <td style="font-size: 14px; padding-top: 20px;">
          {{ $t('logs.signatureLabel') }}
        </td>
        <td style="text-align: right; font-size: 14px; font-weight: bold; padding-top: 20px;">
          {{ companyData.company_name }}
        </td>
      </tr>
    </table>
  </div>
</template>

<script>
import { roundUpIqd } from '../../mixins/helpers'

export default {
  props: {
    companyData: { default: () => ({}) },
    invoiceData: { default: () => ({ customer: {}, orders: [], second_orders: [] }) },
    exRate: { default: 1 }
  },

  computed: {
    totalUsd() {
      const val = this.invoiceData.discount > 0
        ? this.invoiceData.net_price
        : this.invoiceData.total_price
      return Number(val || 0).toFixed(2)
    },
    totalIqd() {
      const raw = Number(this.totalUsd) * Number(this.exRate)
      return roundUpIqd(raw).toLocaleString()
    },
    allOrders() {
      const orders = this.invoiceData.orders || []
      const secondOrders = this.invoiceData.second_orders || []
      return [...orders, ...secondOrders]
    },
    paymentMethodLabel() {
      const map = {
        cash: this.$t('common.cash'),
        fib: 'FIB',
        qi: 'QI Card',
        office: this.$t('invoice.officeOption'),
        out_office: this.$t('columns.outOffice'),
        fastpay: 'Fast Pay',
        balance: this.$t('recordInvoicePayment.balanceOption')
      }
      return map[this.invoiceData.payment_method] || null
    },
    // Matches the badge wording/colors already used for payment_status in orderInvoices.vue.
    paymentStatusLabel() {
      const map = {
        paid: this.$t('invoice.paidStatus'),
        partial_paid: this.$t('invoice.partialPaidStatus'),
        not_paid: this.$t('invoice.unpaidStatus'),
        cancel: this.$t('invoice.cancelStatus')
      }
      return map[this.invoiceData.payment_status] || null
    },
    paymentStatusColor() {
      // Auto-generated invoices (created automatically at order approval, never yet touched by
      // staff via Generate Invoice — see edit_count) show grey instead of the urgent red used for
      // a genuinely unpaid invoice, matching orderInvoices.vue's list badge.
      if (this.invoiceData.payment_status === 'not_paid' && !Number(this.invoiceData.edit_count)) {
        return { bg: '#E0E0E0', color: '#616161' }
      }

      const map = {
        paid: { bg: '#89D7B7', color: '#1A5940' },
        partial_paid: { bg: '#FFC85C', color: '#6B4C00' },
        not_paid: { bg: '#F63049', color: '#FFFFFF' },
        cancel: { bg: '#FB8C00', color: '#FFFFFF' }
      }
      return map[this.invoiceData.payment_status] || { bg: '#eee', color: '#333' }
    },
    // Invoice-level delivery_price (USD) → IQD
    deliveryIqd() {
      const raw = Number(this.invoiceData.delivery_price || 0) * Number(this.exRate)
      return roundUpIqd(raw).toLocaleString()
    },
    // Invoice-level local_delivery (IQD)
    localDeliveryIqd() {
      const raw = Number(this.invoiceData.local_delivery || 0)
      return roundUpIqd(raw).toLocaleString()
    },
    localDeliveryUsd() {
      const rate = Number(this.exRate) || 1
      return (Number(this.invoiceData.local_delivery || 0) / rate).toFixed(2)
    },
    // Order-level delivery_price sum (USD) — not shown in line totals
    ordersDeliveryUsdTotal() {
      return this.allOrders.reduce((sum, o) => sum + Number(o.delivery_price || 0), 0)
    },
    ordersDeliveryIqd() {
      const raw = this.ordersDeliveryUsdTotal * Number(this.exRate)
      return roundUpIqd(raw).toLocaleString()
    },
    // Order-level local_delivery sum (IQD) — not shown in line totals
    ordersLocalDeliveryIqdTotal() {
      return this.allOrders.reduce((sum, o) => sum + Number(o.local_delivery || 0), 0)
    },
    ordersLocalDeliveryIqd() {
      return roundUpIqd(this.ordersLocalDeliveryIqdTotal).toLocaleString()
    },
    ordersLocalDeliveryUsd() {
      const rate = Number(this.exRate) || 1
      return (this.ordersLocalDeliveryIqdTotal / rate).toFixed(2)
    }
  },

  methods: {
    /** Per-unit USD: item_price + internal_shipping_handling (int. ship. hidden from its own row). */
    orderUnitPriceCombined(order) {
      return Number(order.item_price || 0) + Number(order.internal_shipping_handling || 0)
    },
    // The order's own total_amount is the actual charged amount — NOT unit price × quantity.
    // Those can diverge (e.g. after an "Edit Charge" gear-icon correction changes the total
    // without proportionally changing item_price, or when other fees are folded into
    // total_amount), so this must read total_amount directly rather than recomputing it, or the
    // row shows a number that was never actually charged.
    orderLineTotalCombined(order) {
      return Number(order.total_amount || 0)
    }
  }
}
</script>

<style>
.not-paid-watermark {
  position: absolute;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-30deg);
  font-size: 80px;
  font-weight: bold;
  color: rgba(200, 0, 0, 0.18);
  pointer-events: none;
  z-index: 10;
  white-space: nowrap;
  user-select: none;
}

@media screen and (max-width: 650px) {
  #credit-table-header {
    width: 500px;
    padding-left: 10px;
  }
  #price-section2 {
    width: 500px;
    padding-left: 10px;
    padding-top: 10px;
    font-size: 15px;
  }
  #price-section {
    width: 280px;
  }
  #company-info {
    width: 100%;
  }
  #header-img {
    width: 60px;
    height: 60px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/orderInvoice/OrderInvoiceDocument.vue